import { useState } from "react";
import { Link } from "react-router-dom";
import { artists } from "@/mocks/artistData";
import Navbar from "@/pages/home/components/Navbar";

const questTypes = [
  { type: "mv", icon: "ri-film-line", color: "#ff6eb4", label: "뮤직비디오 신", desc: "팬들이 MV 장면을 AI로 제작" },
  { type: "dance", icon: "ri-music-2-line", color: "#4fc3f7", label: "안무 영상", desc: "포인트 안무, 칼군무 등" },
  { type: "cover", icon: "ri-mic-line", color: "#ffd60a", label: "커버 영상", desc: "노래/댄스 커버 영상" },
  { type: "challenge", icon: "ri-trophy-line", color: "#c77dff", label: "챌린지", desc: "특정 미션 수행 영상" },
];

const difficulties = [
  { id: "Easy", label: "입문", stars: 1, color: "#a8e063", desc: "누구나 쉽게 참여 가능" },
  { id: "Medium", label: "중급", stars: 3, color: "#ffd60a", desc: "어느 정도 경험 필요" },
  { id: "Hard", label: "고급", stars: 5, color: "#ff4757", desc: "높은 완성도 요구" },
];

const styleOptions = ["시네마틱", "애니메이션", "실사", "판타지", "감성", "강렬한", "우아한", "역동적"];

export default function QuestNewPage() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    questType: "mv",
    artistId: "",
    difficulty: "Easy",
    reward: "500",
    deadline: "7",
    styles: [] as string[],
    promptHint: "",
    maxParticipants: "",
    isPublic: true,
  });

  const update = (key: string, value: string | boolean | string[]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const toggleStyle = (s: string) =>
    update("styles", form.styles.includes(s) ? form.styles.filter((x) => x !== s) : [...form.styles, s]);

  const selectedArtist = artists.find((a) => a.id === form.artistId);
  const selectedQuestType = questTypes.find((q) => q.type === form.questType);
  const selectedDifficulty = difficulties.find((d) => d.id === form.difficulty);

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center px-6">
        <Navbar />
        <div className="text-center max-w-md mx-auto pt-20">
          <div className="w-24 h-24 flex items-center justify-center rounded-3xl mx-auto mb-6 bg-[#ff6eb4]/20 border-2 border-[#ff6eb4]/50">
            <i className="ri-film-line text-5xl text-[#ff6eb4]" />
          </div>
          <h2 className="text-3xl font-black text-white mb-3">캠페인 생성 완료!</h2>
          <p className="text-white/50 text-base mb-2">
            <span className="font-bold text-[#ff6eb4]">&ldquo;{form.title}&rdquo;</span> 캠페인이 등록되었습니다.
          </p>
          <p className="text-white/30 text-sm mb-8">팬들이 곧 참여를 시작할 거예요!</p>
          <div className="flex flex-col gap-3">
            <Link to="/quests" className="w-full py-3.5 rounded-xl font-bold text-white text-center cursor-pointer whitespace-nowrap" style={{ background: "linear-gradient(135deg, #ff6eb4, #4fc3f7)" }}>
              캠페인 목록 보기
            </Link>
            <button onClick={() => setSubmitted(false)}
              className="w-full py-3.5 rounded-xl font-bold text-white/60 text-center border border-white/15 hover:border-white/30 hover:text-white transition-all cursor-pointer whitespace-nowrap">
              새 캠페인 만들기
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Navbar />

      {/* Header */}
      <div className="pt-24 pb-8 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2 text-sm text-white/40 mb-5">
            <Link to="/" className="hover:text-white/70 transition-colors cursor-pointer">홈</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <Link to="/quests" className="hover:text-white/70 transition-colors cursor-pointer">캠페인</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-white/70">캠페인 생성</span>
          </div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#ff6eb4]/20 to-[#4fc3f7]/20 border border-white/10">
              <i className="ri-add-circle-line text-xl text-[#ff6eb4]" />
            </div>
            <h1 className="text-3xl font-black text-white">새 캠페인 만들기</h1>
          </div>
          <p className="text-white/40 text-sm">팬들이 참여할 뮤직비디오 신 또는 안무 캠페인을 출제하세요.</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 pb-20">
        <div className="flex flex-col lg:flex-row gap-8">

          {/* Main Form */}
          <div className="flex-1 flex flex-col gap-6">

            {/* 캠페인 유형 */}
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <h3 className="text-white font-bold text-base mb-4 flex items-center gap-2">
                <i className="ri-film-line text-[#ff6eb4]" /> 캠페인 유형
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {questTypes.map((qt) => (
                  <button key={qt.type} onClick={() => update("questType", qt.type)}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border text-center transition-all cursor-pointer ${form.questType === qt.type ? "border-[#ff6eb4]/50 bg-[#ff6eb4]/10" : "border-white/10 bg-white/3 hover:border-white/20"}`}>
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ background: `${qt.color}18` }}>
                      <i className={`${qt.icon} text-lg`} style={{ color: qt.color }} />
                    </div>
                    <span className="text-white text-xs font-semibold">{qt.label}</span>
                    <span className="text-white/30 text-[10px]">{qt.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* 기본 정보 */}
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <h3 className="text-white font-bold text-base mb-4 flex items-center gap-2">
                <i className="ri-edit-line text-[#4fc3f7]" /> 캠페인 정보
              </h3>
              <div className="flex flex-col gap-4">
                <div>
                  <label className="text-white/60 text-xs font-semibold mb-2 block">캠페인 제목 *</label>
                  <input type="text" value={form.title} onChange={(e) => update("title", e.target.value)}
                    placeholder="예: SEOL의 벚꽃 뮤직비디오 신 제작"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/20 outline-none focus:border-[#ff6eb4]/50 transition-colors"
                    maxLength={50} />
                  <div className="flex justify-end mt-1"><span className="text-white/20 text-xs">{form.title.length}/50</span></div>
                </div>
                <div>
                  <label className="text-white/60 text-xs font-semibold mb-2 block">캠페인 설명 *</label>
                  <textarea value={form.description} onChange={(e) => update("description", e.target.value)}
                    placeholder="팬들에게 전달할 캠페인 미션을 상세히 설명해주세요."
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/20 outline-none focus:border-[#ff6eb4]/50 transition-colors resize-none"
                    rows={3} maxLength={300} />
                </div>
                <div>
                  <label className="text-white/60 text-xs font-semibold mb-2 block">프롬프트 힌트 (선택)</label>
                  <textarea value={form.promptHint} onChange={(e) => update("promptHint", e.target.value)}
                    placeholder="팬들이 AI 영상 제작 시 참고할 수 있는 프롬프트 힌트를 제공하세요."
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/20 outline-none focus:border-[#ff6eb4]/50 transition-colors resize-none"
                    rows={2} maxLength={200} />
                </div>
                <div>
                  <label className="text-white/60 text-xs font-semibold mb-2 block">권장 스타일 (복수 선택)</label>
                  <div className="flex flex-wrap gap-2">
                    {styleOptions.map((s) => (
                      <button key={s} onClick={() => toggleStyle(s)}
                        className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${form.styles.includes(s) ? "border-[#ff6eb4]/60 bg-[#ff6eb4]/15 text-[#ff6eb4]" : "border-white/10 text-white/50 hover:border-white/20"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* 아티스트 & 난이도 */}
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <h3 className="text-white font-bold text-base mb-4 flex items-center gap-2">
                <i className="ri-user-star-line text-[#ffd60a]" /> 아티스트 & 난이도
              </h3>
              <div className="flex flex-col gap-5">
                <div>
                  <label className="text-white/60 text-xs font-semibold mb-3 block">아티스트 선택 *</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {artists.map((artist) => (
                      <button key={artist.id} onClick={() => update("artistId", artist.id)}
                        className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-all cursor-pointer ${form.artistId === artist.id ? "border-[#ff6eb4]/50 bg-[#ff6eb4]/8" : "border-white/10 bg-white/3 hover:border-white/20"}`}>
                        <img src={artist.avatar} alt={artist.name} className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-white font-semibold text-sm">{artist.name}</p>
                          <p className="text-white/40 text-xs">{artist.groupName}</p>
                        </div>
                        {form.artistId === artist.id && (
                          <div className="ml-auto w-5 h-5 flex items-center justify-center rounded-full bg-[#ff6eb4] flex-shrink-0">
                            <i className="ri-check-line text-white text-xs" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-white/60 text-xs font-semibold mb-3 block">난이도</label>
                  <div className="flex gap-3 flex-wrap">
                    {difficulties.map((d) => (
                      <button key={d.id} onClick={() => update("difficulty", d.id)}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${form.difficulty === d.id ? "text-white" : "border-white/10 text-white/50 hover:border-white/20"}`}
                        style={form.difficulty === d.id ? { borderColor: `${d.color}60`, background: `${d.color}15`, color: d.color } : {}}>
                        <span>{"★".repeat(d.stars)}</span>
                        {d.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* 보상 & 기간 */}
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <h3 className="text-white font-bold text-base mb-4 flex items-center gap-2">
                <i className="ri-coin-line text-[#c77dff]" /> 보상 & 기간
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-white/60 text-xs font-semibold mb-2 block">XP 보상</label>
                  <div className="flex gap-2 flex-wrap">
                    {["300", "500", "1000", "2000", "5000"].map((xp) => (
                      <button key={xp} onClick={() => update("reward", xp)}
                        className={`px-3 py-1.5 rounded-lg border text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${form.reward === xp ? "border-[#ffd60a]/60 bg-[#ffd60a]/15 text-[#ffd60a]" : "border-white/10 text-white/50 hover:border-white/20"}`}>
                        {parseInt(xp).toLocaleString()} XP
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-white/60 text-xs font-semibold mb-2 block">마감 기간</label>
                  <div className="flex gap-2 flex-wrap">
                    {["3", "7", "14", "21", "30"].map((d) => (
                      <button key={d} onClick={() => update("deadline", d)}
                        className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${form.deadline === d ? "border-[#4fc3f7]/60 bg-[#4fc3f7]/15 text-[#4fc3f7]" : "border-white/10 text-white/50 hover:border-white/20"}`}>
                        {d}일
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-white/60 text-xs font-semibold mb-2 block">최대 참여 인원 (선택)</label>
                  <input type="number" value={form.maxParticipants} onChange={(e) => update("maxParticipants", e.target.value)}
                    placeholder="제한 없음"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/20 outline-none focus:border-[#ff6eb4]/50 transition-colors" />
                </div>
              </div>
            </div>

            {/* Submit */}
            <button
              onClick={() => { if (form.title && form.artistId) setSubmitted(true); }}
              disabled={!form.title || !form.artistId}
              className="w-full py-4 rounded-xl font-bold text-base text-white cursor-pointer whitespace-nowrap hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ background: "linear-gradient(135deg, #ff6eb4, #4fc3f7)" }}
            >
              <i className="ri-add-circle-line text-xl" />
              캠페인 생성하기
            </button>
          </div>

          {/* Preview Sidebar */}
          <div className="w-full lg:w-72 flex-shrink-0">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5 sticky top-24">
              <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-4">미리보기</h4>

              {/* Campaign Card Preview */}
              <div className="rounded-xl border border-white/10 bg-[#0f0f1a] overflow-hidden">
                <div className="relative h-32 bg-gradient-to-br from-[#ff6eb4]/20 to-[#4fc3f7]/20 flex items-center justify-center">
                  {selectedQuestType && (
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl" style={{ background: `${selectedQuestType.color}20` }}>
                      <i className={`${selectedQuestType.icon} text-2xl`} style={{ color: selectedQuestType.color }} />
                    </div>
                  )}
                  <div className="absolute top-2 left-2">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#ff6eb4]/20 text-[#ff6eb4]">
                      {selectedQuestType?.label || "캠페인 유형"}
                    </span>
                  </div>
                  <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/50 rounded-full px-2 py-0.5">
                    <i className="ri-coin-line text-[#ffd60a] text-xs" />
                    <span className="text-white text-xs font-bold">{parseInt(form.reward).toLocaleString()} XP</span>
                  </div>
                </div>
                <div className="p-3">
                  {selectedArtist && (
                    <div className="flex items-center gap-1.5 mb-2">
                      <img src={selectedArtist.avatar} alt={selectedArtist.name} className="w-4 h-4 rounded-full object-cover" />
                      <span className="text-white/40 text-xs">{selectedArtist.name} · {selectedArtist.groupName}</span>
                    </div>
                  )}
                  <p className="text-white font-semibold text-xs mb-1 line-clamp-1">{form.title || "캠페인 제목"}</p>
                  <p className="text-white/40 text-[10px] line-clamp-2">{form.description || "캠페인 설명이 여기에 표시됩니다."}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-white/30 text-[10px]">
                      {selectedDifficulty ? `${"★".repeat(selectedDifficulty.stars)} ${selectedDifficulty.label}` : "난이도"}
                    </span>
                    <span className="text-white/30 text-[10px]">{form.deadline}일 남음</span>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-2">
                {[
                  { label: "캠페인 유형", value: selectedQuestType?.label || "-" },
                  { label: "아티스트", value: selectedArtist ? `${selectedArtist.name} (${selectedArtist.groupName})` : "-" },
                  { label: "난이도", value: selectedDifficulty?.label || "-" },
                  { label: "XP 보상", value: `${parseInt(form.reward).toLocaleString()} XP` },
                  { label: "마감", value: `${form.deadline}일` },
                ].map((item) => (
                  <div key={item.label} className="flex items-center justify-between">
                    <span className="text-white/30 text-xs">{item.label}</span>
                    <span className="text-white/60 text-xs font-medium">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
